import window from '../window/index.js';
import { isClient } from '../utils/index.js';
import raf from './raf.js';
import Idler from './Idler.js';
import { save as saveScroll } from '../common/scrollPosition.js';
import { loop as useRafLoop } from '../hooks/useRAF.js';
import { loop as useResizeLoop } from '../hooks/useResize.js';
import { engine } from '@immutabl3/anime';
import {
  SET_ORCHESTRATOR,
  SCROLL_SAVE_DELAY,
} from '../constants.js';

let flowDirty = false;
let hasRestoredScroll = false;
let width = window.width();
let height = window.height();

const labs = new Set();

const isIdle = Idler(SCROLL_SAVE_DELAY);

const reflow = () => {
  width = window.width();
  height = window.height();

  let maxHeight = 0;
  for (const lab of labs.values()) {
    maxHeight += lab.resize();
  }
  
  window.set('maxScrollTop', maxHeight);
};

// simple loop, getting the scroll position
// and propagating it down to the labs
const loop = timestamp => {
  // queue next animation frame right away
  // so that we're not queued towards the
  // end of a frames' execution. happens
  // when DOM unexpectedly restructures
  raf(loop);
  
  const top = window.scrollTop();

  if (isIdle(top)) saveScroll();

  // mobile safari's bottom drawer sometimes
  // "forgets" to fire a window resize event
  // (or fires it out-of-order) and the drawer
  // behavior is just generally a huge frustration
  // 
  // my solution (right now) is to constantly
  // measure the window and resize when we detect
  // something has changed. it's a little heavy,
  // (0.5ms) but avoids needing to both debounce the 
  // resize event (for performance) & re-fire the
  // resize event because safari

  window.measure();

  const w = window.width();
  const h = window.height();
  if (flowDirty || w !== width || h !== height) {
    useResizeLoop();
    reflow();
    flowDirty = false;
  }

  for (const lab of labs.values()) {
    // NOTE: this is the natural way of restoring
    // scroll. before the window's load event, the
    // browser will update the page's scroll position.
    // we're detecting that here and accounting for it
    if (!hasRestoredScroll && top !== 0) {
      hasRestoredScroll = true;
      lab.jumpTo(top);
    }

    lab.scroll(top);
  }

  engine.step(timestamp);
  useRafLoop(timestamp);
};

isClient && raf(loop);

const orchestrator = {
  reflow,
  jumpTo({
    left = 0,
    top = 0,
  } = {}) {
    window.jumpTo({ top, left });
    for (const lab of labs.values()) {
      lab.jumpTo(top);
    }
  },
  add(lab) {
    labs.add(lab);
    flowDirty = true;
  },
  remove(lab) {
    labs.delete(lab);
    flowDirty = true;
  },
};

window.scrollTo[SET_ORCHESTRATOR](orchestrator);

export default orchestrator;